/**
 * Board motion as pure functions of time. planMotion compares the board
 * before and after a change and schedules what should move; sampleMotion
 * reads that plan at a timestamp. Rendering re-reads it every frame while a
 * plan is active, so the same code drives web and native. No expanding or
 * concentric ring animations anywhere (owner decision).
 */
import type { GameStatus } from "./boardModel";
import type { EdgeMotion } from "./BoardEdges";
import type { Camera } from "./layout";
import { MOTION } from "./motionConfig";
import { buildWave, waveEnd, type Wave } from "./wave";
import {
  optionSwap,
  type GhostOption,
  type SnapshotOption,
} from "./optionMotion";

export type { GhostOption, SnapshotOption };

export { MOTION };

export interface MotionSnapshot {
  gameKey: string;
  path: string[];
  status: GameStatus;
  streak: number;
  levels: number[];
  camera: Camera;
  pos: Record<string, { px: number; py: number }>;
  /** Player edges in path order, with their resting colour and width. */
  playerEdges: { key: string; color: string; width: number }[];
  gold: string;
  pathColor: string;
  /** Next-word options on the board, in button order ("options" mode). */
  options?: SnapshotOption[];
}

export type { Wave };
export { waveEnd };

export interface MotionPlan {
  t0: number;
  end: number;
  /** When the win sequence is over and the report may open. */
  winEnd?: number;
  crunch?: {
    node: string;
    pop: boolean;
    edgeKey: string | null;
    dir: { x: number; y: number };
  };
  glide?: { k0: number; dx0: number; dy0: number };
  wave?: Wave;
  win?: Wave & { edges: { key: string; from: string }[]; gold: string };
  shimmer?: { edges: string[] };
  cool?: { edges: { key: string; fromColor: string }[]; to: string };
  options?: { ghosts: GhostOption[]; born: string[] };
}

export interface MotionFrame {
  active: boolean;
  nodeScale: (id: string) => number;
  camera: { k: number; dx: number; dy: number };
  edge: (key: string) => EdgeMotion | undefined;
  flash: { node: string; opacity: number } | null;
  /** Pop-in scale of a new option node (0 = not shown yet). */
  optionScale: (id: string) => number;
  /** Old options fading out, with their opacity. */
  ghosts: (GhostOption & { opacity: number })[];
  /**
   * During a win wave, how far the last node's (the target's) ring has
   * filled (0 = empty, 1 = filled); null outside one: the model decides.
   */
  targetFill: { node: string; level: number } | null;
}

export function planMotion(
  prev: MotionSnapshot | null,
  next: MotionSnapshot,
  now: number,
  reduced: boolean,
  current: MotionPlan | null = null,
): MotionPlan | null {
  if (!prev || prev.gameKey !== next.gameKey) return null;
  const grew =
    next.path.length === prev.path.length + 1 &&
    prev.path.every((w, i) => next.path[i] === w);
  const won = next.status === "won" && prev.status !== "won";
  if (!grew) {
    if (won) return joinWin(current, next, now, reduced);
    const moved = next.path.join("|") !== prev.path.join("|");
    if (!moved) return current;
    if (reduced) return null;
    const glide = glideFrom(prev.camera, next.camera);
    return glide ? { t0: now, end: now + MOTION.glideMs, glide } : null;
  }

  const u = prev.path[prev.path.length - 1];
  const w = next.path[next.path.length - 1];
  const opt = (next.levels[next.levels.length - 1] ?? 0) > 0;
  const broken = !opt && prev.streak > 0;
  const plan: MotionPlan = { t0: now, end: now };
  const at = (ms: number) => (plan.end = Math.max(plan.end, now + ms));

  if (reduced) {
    if (won) plan.winEnd = now;
    return plan;
  }

  const a = next.pos[u];
  const b = next.pos[w];
  const len = a && b ? Math.hypot(b.px - a.px, b.py - a.py) || 1 : 1;
  const dir =
    a && b
      ? { x: (b.px - a.px) / len, y: (b.py - a.py) / len }
      : { x: 0, y: 0 };
  const edgeKey = next.playerEdges[next.playerEdges.length - 1]?.key ?? null;
  const waveRuns = opt && next.streak >= 2 && !won;
  plan.crunch = { node: w, pop: !waveRuns && !won, edgeKey, dir };
  at(MOTION.nudgeMs);
  const swap = optionSwap(prev, next, u);
  if (swap) {
    plan.options = swap;
    at(
      Math.max(
        MOTION.optionFadeMs,
        swap.born.length
          ? MOTION.optionInDelay +
              (swap.born.length - 1) * MOTION.optionGap +
              MOTION.optionPopMs
          : 0,
      ),
    );
  }
  const glide = glideFrom(prev.camera, next.camera);
  if (glide) plan.glide = glide;

  if (broken) {
    plan.cool = {
      edges: prev.playerEdges
        .filter((_, i) => (prev.levels[i] ?? 0) > 0)
        .map((e) => ({ key: e.key, fromColor: e.color })),
      to: next.pathColor,
    };
    at(MOTION.coolMs);
  }
  if (waveRuns) {
    const nodes = next.path.slice(next.path.length - 1 - next.streak);
    // The wave waits for the camera glide to settle.
    plan.wave = buildWave(nodes, "streak", afterGlide(MOTION.waveDelay, glide));
    at(waveEnd(plan.wave));
  }
  if (opt && next.streak >= 3 && !won) {
    plan.shimmer = {
      edges: next.playerEdges
        .filter((_, i) => (next.levels[i] ?? 0) > 0)
        .map((e) => e.key),
    };
    at(MOTION.drawMs + MOTION.shimmerLoopMs * MOTION.shimmerLoops);
  }
  if (won) {
    plan.win = buildWin(next, afterGlide(MOTION.winDelay, glide));
    plan.winEnd = now + waveEnd(plan.win);
    at(waveEnd(plan.win));
  }
  return plan;
}

const afterGlide = (delay: number, glide: unknown) =>
  glide ? Math.max(delay, MOTION.glideMs) : delay;

/** The win wave: every path node, each edge turning gold as it passes. */
function buildWin(
  next: Pick<MotionSnapshot, "path" | "playerEdges" | "pathColor" | "gold">,
  delay: number,
): NonNullable<MotionPlan["win"]> {
  return {
    ...buildWave(next.path, "win", delay),
    edges: next.playerEdges.map((e, i) => ({
      key: e.key,
      from: i === next.playerEdges.length - 1 ? next.pathColor : e.color,
    })),
    gold: next.gold,
  };
}

/**
 * The store marks a game won a beat after the winning move renders: fold the
 * win into the move's running plan (its streak wave and pop give way to the
 * win wave) instead of restarting.
 */
function joinWin(
  current: MotionPlan | null,
  next: MotionSnapshot,
  now: number,
  reduced: boolean,
): MotionPlan {
  const base: MotionPlan =
    current && now < current.end ? { ...current } : { t0: now, end: now };
  if (reduced) return { ...base, winEnd: now };
  delete base.wave;
  delete base.shimmer;
  if (base.crunch) base.crunch = { ...base.crunch, pop: false };
  const win = buildWin(
    next,
    Math.max(afterGlide(MOTION.winDelay, base.glide), now - base.t0),
  );
  base.win = win;
  base.winEnd = base.t0 + waveEnd(win);
  base.end = Math.max(base.end, base.winEnd);
  return base;
}

/**
 * A replay of the streak or win wave over the board's current path, for the
 * dev tuning panel. It never sets winEnd, so it never opens the report.
 */
export function replayPlan(
  snap: MotionSnapshot,
  kind: "streak" | "win",
  now: number,
): MotionPlan | null {
  if (snap.path.length < 2) return null;
  const plan: MotionPlan = { t0: now, end: now };
  if (kind === "streak") {
    plan.wave = buildWave(snap.path, "streak", MOTION.waveDelay);
    plan.end = now + waveEnd(plan.wave);
  } else {
    plan.win = buildWin(snap, MOTION.winDelay);
    plan.end = now + waveEnd(plan.win);
  }
  return plan;
}

function glideFrom(a: Camera, b: Camera) {
  if (Math.abs(a.s - b.s) < 1e-9 && a.tx === b.tx && a.ty === b.ty) return null;
  const k0 = a.s / b.s;
  return { k0, dx0: a.tx - k0 * b.tx, dy0: a.ty - k0 * b.ty };
}
